/* Tarot swipe deck v3 — fetch/shuffle/render + drag-swipe + keyboard +
   wishlist wiring + "Draw your fate" slot roll + The Goblin rare
   discount card, now with real artwork. Everything is scoped to each
   `.gwtd` section instance. */
(function () {
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // v9: the burn effect (buildBurnMask, below) needs a dynamically-built
  // SVG <mask> with SMIL <animate> children, applied via CSS
  // mask-composite — confirmed live that iOS/desktop Safari don't
  // render this reliably (the mask never visibly dissolves, leaving
  // only the plain CSS glow/ember layers playing on top of an
  // untouched card — "just an orange flash and flicker"). Rather than
  // risk ever showing that broken half-state, phones/touch devices and
  // any non-Chromium desktop browser get the swipe-stamp fallback
  // instead — never the burn. window.chrome is a real, if informal,
  // Chromium fingerprint (present in Chrome/Edge/Opera/Brave, absent in
  // Safari/Firefox); combined with a touch check and a CSS.supports
  // check, this errs toward the safe (swipe) path whenever uncertain.
  // v9.2: was `(hover: none) AND (pointer: coarse)` — requiring BOTH
  // conditions turned out to be too strict for some real Android/Chrome
  // configurations (confirmed as the actual cause: an Android phone
  // that reports pointer:coarse correctly but not hover:none for
  // whatever reason would fail the AND and fall through to the
  // burn-eligible path, which is exactly the "Android Chrome still gets
  // the burn" bug reported). Either signal alone is now enough — a
  // touchscreen reporting ONE coarse-pointer/no-hover signal is still a
  // touch device, full stop, regardless of what the other reports.
  function isTouchDevice() {
    if (!window.matchMedia) return false;
    return window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches;
  }
  function supportsBurnEffect() {
    if (isTouchDevice()) return false;
    try {
      var isChromium = !!window.chrome;
      var maskOk = typeof CSS !== 'undefined' && CSS.supports &&
        (CSS.supports('mask-composite', 'exclude') || CSS.supports('-webkit-mask-composite', 'xor'));
      return !!(isChromium && maskOk);
    } catch (e) {
      return false;
    }
  }
  var USE_SWIPE_STAMP = !supportsBurnEffect();

  var BATCH_LIMIT = 30;
  // v9.2: how many random pages fetchNextBatch() pulls in parallel per
  // call — the actual fix for the "30 coasters in a row" bug (see the
  // comment above interleaveByType()).
  var BATCH_PAGES_PER_FETCH = 4;
  var REFILL_THRESHOLD = 10;
  var SWIPE_THRESHOLD_PX = 90;
  var DRAG_LOCK_PX = 6; // pointer movement below this (in either axis) = a tap, not a drag
  var MAX_PAGE_CANDIDATES = 40;
  var GOBLIN_SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000;
  var GOBLIN_STORAGE_KEY = 'gw_goblin_claimed_at';

  var TITLE_SUFFIXES = [' - Goblin Wood Exclusive', ' - Sumthin Wicked Exclusive'];

  var EMBLEMS = {
    crossed_blades: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M4 4l16 16M20 4L4 20"/><path d="M4 4l2.5.5M4 4l.5 2.5M20 4l-2.5.5M20 4l-.5 2.5M4 20l2.5-.5M4 20l.5-2.5M20 20l-2.5-.5M20 20l-.5-2.5"/></svg>',
    fangs: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 4c-1.5 0-2.5 1-2.5 3 0 3 1 7 2 7s1.5-2 1.5-5V4z"/><path d="M16 4c1.5 0 2.5 1 2.5 3 0 3-1 7-2 7s-1.5-2-1.5-5V4z"/></svg>',
    stake: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 2h2v13h-2z"/><path d="M12 15l4 3-4 5-4-5z"/><path d="M7 6h10v2H7z"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.5 6.8 19.1l1-5.8L3.5 9.2l5.9-.9L12 3z"/></svg>',
    moon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>',
    eye: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>',
    skull: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3a7 7 0 0 0-7 7c0 3 1.5 4.5 2 6h10c.5-1.5 2-3 2-6a7 7 0 0 0-7-7z"/><circle cx="9.5" cy="10" r="1.4" fill="#000"/><circle cx="14.5" cy="10" r="1.4" fill="#000"/><path d="M10 17h4v2a2 2 0 0 1-4 0z"/></svg>',
    flame: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c-1 3-4 5-4 9a4 4 0 0 0 8 0c0-1-.4-1.8-1-2.4.2 1.2-.6 2-1.4 1.6.8-1.6-.2-3-1-4.2.4 1.6-.8 2.4-1.8 1.8.8-1.2.8-3.2 1.2-5.8z"/></svg>'
  };

  var FORCE_GOBLIN_REQUESTED = (function () {
    try {
      return new URLSearchParams(window.location.search).get('goblin') === '1';
    } catch (e) {
      return false;
    }
  })();

  // ── Burn-away mask (A1 rebuild) ──────────────────────────────────
  // Builds a one-off <svg><filter>+<mask> per burning card: a single
  // rect, fed through feTurbulence + feDisplacementMap (+ a touch of
  // feGaussianBlur for softness), shrinks from full-card to nothing via
  // native SMIL <animate> (no per-frame JS/layout work — the browser's
  // own SVG engine drives it, which keeps this smooth on phones). The
  // displaced/blurred rect edge becomes a ragged, noisy "fire line"
  // instead of a clean geometric shape. Applied as mask-image on
  // .gwtd__card-face (so the real art/media/numeral/name dissolve) and
  // on .gwtd__card-burn-glow (so the glow only shows right at the
  // crumbling edge, not as a flat overlay).
  //
  // Deliberately in absolute pixel units (measured from the card's own
  // getBoundingClientRect() at burn-start), NOT primitiveUnits=
  // objectBoundingBox — tested that first and feTurbulence/
  // feDisplacementMap support for objectBoundingBox primitive units is
  // inconsistent enough (confirmed live: it made the displacement scale
  // wildly larger than intended, so the card looked almost fully
  // dissolved less than halfway through the animation) that it isn't
  // reliable to build on. Pixel units are the standard, well-supported
  // technique for this kind of effect. Recomputed fresh per burn (cheap,
  // happens once per card, not per frame), so it still works correctly
  // at any card size/breakpoint. The whole <svg> lives INSIDE cardEl, so
  // it's removed automatically with the card — nothing to clean up.
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var burnMaskCounter = 0;

  function addBurnAnim(rect, attr, from, to, durMs) {
    var anim = document.createElementNS(SVG_NS, 'animate');
    anim.setAttribute('attributeName', attr);
    anim.setAttribute('from', String(from));
    anim.setAttribute('to', String(to));
    anim.setAttribute('dur', (durMs / 1000) + 's');
    anim.setAttribute('fill', 'freeze');
    anim.setAttribute('calcMode', 'spline');
    anim.setAttribute('keyTimes', '0;1');
    anim.setAttribute('keySplines', '0.3 0 0.7 1');
    rect.appendChild(anim);
  }

  // v7: rebuilt so the card burns inward from ALL FOUR EDGES and closes
  // in on the centre (not toward one corner). Two rects share the same
  // ragged noise filter and the same shrink-to-centre timing:
  //  - the CONTENT rect shrinks from the full card (0,0,W,H) down to a
  //    point at the centre (W/2,H/2,0,0) — masks .gwtd__card-face, so
  //    this is literally where the art/media/text disappears.
  //  - the RING rect follows the identical shrink but stays RING_PX
  //    larger on every side the whole time, so it's always just behind
  //    (outside) the content edge. Combining the two on the glow layer
  //    with mask-composite:exclude (true set-difference between them)
  //    leaves ONLY the thin band between the two edges lit — a real
  //    ember rim that tracks the retreating burn line the whole way,
  //    not a flat glow filling the whole hole.
  function buildBurnMask(cardEl, durMs) {
    burnMaskCounter += 1;
    var id = 'gwtd-burn-' + burnMaskCounter + '-' + Date.now();

    var box = cardEl.getBoundingClientRect();
    var W = Math.max(40, Math.round(box.width));
    var H = Math.max(40, Math.round(box.height));
    // v8: widened (was 0.05) — the rim was correct but too thin to read
    // as an obvious glow at normal speed, on top of being invisible
    // outright until the div:empty fix (see gw-tarot-deck.css).
    var RING = Math.max(18, Math.round(W * 0.1));

    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.style.position = 'absolute';
    svg.style.width = '0';
    svg.style.height = '0';
    svg.style.overflow = 'hidden';

    var defs = document.createElementNS(SVG_NS, 'defs');

    function buildEdgeFilter(filterId, seed) {
      var filter = document.createElementNS(SVG_NS, 'filter');
      filter.setAttribute('id', filterId);
      filter.setAttribute('x', '-30%');
      filter.setAttribute('y', '-30%');
      filter.setAttribute('width', '160%');
      filter.setAttribute('height', '160%');
      filter.setAttribute('filterUnits', 'objectBoundingBox');

      var turb = document.createElementNS(SVG_NS, 'feTurbulence');
      turb.setAttribute('type', 'fractalNoise');
      turb.setAttribute('baseFrequency', '0.018 0.028');
      turb.setAttribute('numOctaves', '2');
      turb.setAttribute('seed', String(seed));
      turb.setAttribute('result', 'gwtdnoise');

      var disp = document.createElementNS(SVG_NS, 'feDisplacementMap');
      disp.setAttribute('in', 'SourceGraphic');
      disp.setAttribute('in2', 'gwtdnoise');
      disp.setAttribute('scale', String(Math.round(W * 0.16)));
      disp.setAttribute('xChannelSelector', 'R');
      disp.setAttribute('yChannelSelector', 'G');
      disp.setAttribute('result', 'gwtddisp');

      var blur = document.createElementNS(SVG_NS, 'feGaussianBlur');
      blur.setAttribute('in', 'gwtddisp');
      blur.setAttribute('stdDeviation', String(Math.max(1, Math.round(W * 0.006))));

      filter.appendChild(turb);
      filter.appendChild(disp);
      filter.appendChild(blur);
      return filter;
    }

    // Same seed on both filters so the ring's noisy edge and the
    // content's noisy edge are displaced the same way and visually line
    // up as one burn line, not two independently-wobbling edges.
    var sharedSeed = Math.floor(Math.random() * 200);
    var contentFilter = buildEdgeFilter(id + '-edge-content', sharedSeed);
    var ringFilter = buildEdgeFilter(id + '-edge-ring', sharedSeed);

    function buildShrinkMask(maskId, filterId, inset) {
      var mask = document.createElementNS(SVG_NS, 'mask');
      mask.setAttribute('id', maskId);
      mask.setAttribute('maskUnits', 'objectBoundingBox');
      mask.setAttribute('maskContentUnits', 'userSpaceOnUse');
      mask.setAttribute('x', '0');
      mask.setAttribute('y', '0');
      mask.setAttribute('width', '1');
      mask.setAttribute('height', '1');

      var rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('fill', '#fff');
      rect.setAttribute('filter', 'url(#' + filterId + ')');

      // Shrinks from the full card (minus `inset`, so the ring variant
      // starts RING_PX bigger than the card itself) down to a point
      // dead centre — eats in from every edge at once.
      addBurnAnim(rect, 'x', -inset, W / 2, durMs);
      addBurnAnim(rect, 'y', -inset, H / 2, durMs);
      addBurnAnim(rect, 'width', W + inset * 2, 0, durMs);
      addBurnAnim(rect, 'height', H + inset * 2, 0, durMs);

      mask.appendChild(rect);
      return mask;
    }

    var contentMask = buildShrinkMask(id + '-mask-content', id + '-edge-content', 0);
    var ringMask = buildShrinkMask(id + '-mask-ring', id + '-edge-ring', RING);

    defs.appendChild(contentFilter);
    defs.appendChild(ringFilter);
    defs.appendChild(contentMask);
    defs.appendChild(ringMask);
    svg.appendChild(defs);
    cardEl.appendChild(svg);

    // The <mask> is scoped to viewBox-less userSpaceOnUse content inside
    // an objectBoundingBox-sized region, which only maps 1:1 to the
    // masked element's own box if that element is exactly W×H — true
    // here since .gwtd__card-face/.gwtd__card-burn-glow are inset:0 on
    // a card already measured at W×H above.
    var contentMaskUrl = 'url(#' + id + '-mask-content)';
    var ringMaskUrl = 'url(#' + id + '-mask-ring)';
    var face = cardEl.querySelector('.gwtd__card-face');
    var glow = cardEl.querySelector('.gwtd__card-burn-glow');
    if (face) {
      face.style.webkitMaskImage = contentMaskUrl;
      face.style.maskImage = contentMaskUrl;
    }
    if (glow) {
      // Two mask layers, XORed: ring (bigger, lags behind) minus
      // content (smaller, exact edge) = just the thin band between
      // them — the actual ember rim, not a flat fill of the hole.
      glow.style.webkitMaskImage = ringMaskUrl + ', ' + contentMaskUrl;
      glow.style.webkitMaskComposite = 'xor';
      glow.style.maskImage = ringMaskUrl + ', ' + contentMaskUrl;
      glow.style.maskComposite = 'exclude';
    }
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
    }
    return arr;
  }

  function range(n) {
    var out = [];
    for (var i = 1; i <= n; i++) out.push(i);
    return out;
  }

  function formatMoney(priceStr) {
    var n = parseFloat(priceStr);
    if (isNaN(n)) return '';
    return '£' + n.toFixed(2);
  }

  function cdnResize(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function productIsAvailable(product) {
    return product.variants && product.variants.some(function (v) { return v.available; });
  }
  function availableVariants(product) {
    return (product.variants || []).filter(function (v) { return v.available; });
  }

  // Strips the known franchise-exclusive suffixes for display in the
  // deck only (never touches the real product.title used for cart/url).
  function cleanTitle(title) {
    var clean = title;
    TITLE_SUFFIXES.forEach(function (suffix) {
      if (clean.slice(-suffix.length).toLowerCase() === suffix.toLowerCase()) {
        clean = clean.slice(0, -suffix.length);
      }
    });
    return clean;
  }

  // v9.2: root cause of "30 coasters in a row" — the old single-page
  // fetch (one random /products.json?page=N of 30) could come back
  // almost entirely one product type whenever the collection's default
  // sort groups similar products together (confirmed: Shopify
  // collections commonly sort by a fixed order where e.g. all coasters
  // sit adjacent). The old deduplicateAdjacentTypes() this replaced
  // only ever swapped items WITHIN whatever came back from that one
  // page — powerless against a batch with no variety in it to begin
  // with. It was also a near no-op independently of that: it keyed on
  // raw product.product_type, which this catalogue leaves blank on
  // most products (type is normally derived from collections/tags
  // elsewhere in this theme, not Shopify's own product_type field), so
  // almost every adjacent pair compared blank===blank and the "swap"
  // search never found a genuinely different type to trade with.
  //
  // Fixed two ways: fetchNextBatch() now pulls several random pages in
  // one batch (merged), so there's real variety to work with even if
  // any single page is homogeneous; and type is guessed from the title
  // whenever product_type is blank, so the round-robin below actually
  // has real type buckets to interleave instead of one big "blank" one.
  var TYPE_GUESS_PATTERNS = [
    { type: 'Coaster', re: /coaster/i },
    { type: 'Hoody', re: /\bhood(y|ie)\b/i },
    { type: 'Pin Badge', re: /pin badge|enamel pin/i },
    { type: 'Backpack', re: /backpack/i },
    { type: 'Mug', re: /\bmug\b/i },
    { type: 'Blanket', re: /blanket/i },
    { type: 'Tote', re: /\btote\b/i },
    { type: 'Keychain', re: /key\s?(chain|ring|fob)/i },
    { type: 'Purse', re: /purse|wallet|crossbody/i },
    { type: 'T-Shirt', re: /\bt-?shirts?\b|\btee\b/i },
    { type: 'Vinyl', re: /funko|pop! vinyl/i },
    { type: 'Candle', re: /candle/i },
    { type: 'Socks', re: /\bsocks\b/i },
    { type: 'Journal', re: /journal|notebook/i },
    { type: 'Phone Grip', re: /phone grip|pop socket/i },
    { type: 'Hat', re: /\b(cap|beanie|hat)\b/i }
  ];
  function guessProductType(product) {
    if (product.product_type) return product.product_type;
    var title = product.title || '';
    for (var i = 0; i < TYPE_GUESS_PATTERNS.length; i++) {
      if (TYPE_GUESS_PATTERNS[i].re.test(title)) return TYPE_GUESS_PATTERNS[i].type;
    }
    return 'Other';
  }

  // products.json returns tags as an array of strings; /products/{handle}.js
  // (the card/variant fetch used elsewhere in this file) can return them as
  // a single comma-separated string instead — handle both.
  function isPreorder(product) {
    var tags = product.tags;
    if (!tags) return false;
    var list = Array.isArray(tags) ? tags : String(tags).split(',');
    for (var i = 0; i < list.length; i++) {
      var t = String(list[i]).trim().toLowerCase();
      if (t === 'pre-order' || t === 'preorder') return true;
    }
    return false;
  }

  // Round-robin by (guessed) type: never more than 2 of the same type
  // back to back while a different type still has items waiting.
  // seedType/seedCount carry the run-so-far from whatever was dealt
  // just before this batch (see dealCard()'s state.lastDealtType) —
  // found live (via a 40-card swipe test) that without this, a fresh
  // call starting its own count at 0 could let the SAME type that was
  // already 2-in-a-row at the front of the queue extend to 3 the
  // instant a new batch arrived and got re-interleaved mid-run.
  // Items that can't be placed without breaking the rule are returned as
  // `leftover` and held in state.pool until more types arrive; only the
  // final batch of a pass (flush=true) is allowed to break the rule.
  function interleaveByType(list, seedType, seedCount, flush) {
    var buckets = {};
    var order = [];
    list.forEach(function (p) {
      var t = guessProductType(p);
      if (!buckets[t]) { buckets[t] = []; order.push(t); }
      buckets[t].push(p);
    });
    var result = [];
    var lastType = seedType || null, lastTypeCount = seedCount || 0, remaining = list.length;
    while (remaining > 0) {
      var chosenType = null;
      var startIdx = lastType ? (order.indexOf(lastType) + 1) : 0;
      for (var k = 0; k < order.length; k++) {
        var idx = (startIdx + k) % order.length;
        var t = order[idx];
        if (!buckets[t].length) continue;
        if (t === lastType && lastTypeCount >= 2) continue;
        chosenType = t;
        break;
      }
      if (!chosenType) {
        if (!flush) break;
        chosenType = lastType;
      }
      var item = buckets[chosenType].shift();
      result.push(item);
      remaining--;
      if (chosenType === lastType) lastTypeCount++;
      else { lastType = chosenType; lastTypeCount = 1; }
    }
    var leftover = [];
    order.forEach(function (t) { leftover = leftover.concat(buckets[t]); });
    return { result: result, leftover: leftover };
  }

  function isGoblinSuppressed() {
    try {
      var ts = localStorage.getItem(GOBLIN_STORAGE_KEY);
      if (!ts) return false;
      return (Date.now() - parseInt(ts, 10)) < GOBLIN_SUPPRESS_MS;
    } catch (e) {
      return false;
    }
  }
  function markGoblinClaimed() {
    try { localStorage.setItem(GOBLIN_STORAGE_KEY, String(Date.now())); } catch (e) {}
  }

  function initSection(sectionEl) {
    // Christmas mode (B1): swap "wishlist" for "Christmas list" in the
    // merchant-authored subheading text, in place, without touching the
    // inline heart SVG split into it — only text nodes are rewritten.
    var subheadingEl = sectionEl.querySelector('.gwtd__subheading');
    if (subheadingEl && window.gwWishlist && window.gwWishlist.isChristmasMode && window.gwWishlist.isChristmasMode()) {
      var walker = document.createTreeWalker(subheadingEl, NodeFilter.SHOW_TEXT);
      var textNode;
      while ((textNode = walker.nextNode())) {
        textNode.textContent = textNode.textContent.replace(/wishlist/gi, 'Christmas list');
      }
    }

    var dataScript = sectionEl.querySelector('[data-gwtd-arcana-data]');
    if (!dataScript) return;
    var arcanaPool;
    try {
      arcanaPool = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }
    if (!arcanaPool.length) return;

    var goblinConfig = { enabled: false, odds: 150, code: '', intro: '', headline: '', subline: '', allowForceTest: false };
    var goblinScript = sectionEl.querySelector('[data-gwtd-goblin-config]');
    if (goblinScript) {
      try { goblinConfig = JSON.parse(goblinScript.textContent); } catch (e) {}
    }
    // The test hook only ever fires when the server (Liquid, based on
    // theme.role) said it's allowed — on the published/live theme that
    // flag is always false, baked into the page itself, so there's no
    // client-side way to flip it on here.
    var forceGoblin = !!(goblinConfig.allowForceTest && FORCE_GOBLIN_REQUESTED);

    var cardTemplate = sectionEl.querySelector('[data-gwtd-card-template]');
    var goblinTemplate = sectionEl.querySelector('[data-gwtd-goblin-template]');

    var deckEls = Array.prototype.slice.call(sectionEl.querySelectorAll('[data-gwtd-deck]'));
    var decks = deckEls.map(function (deckEl) {
      return initDeck(deckEl, arcanaPool, goblinConfig, forceGoblin, cardTemplate, goblinTemplate);
    });

    var chips = Array.prototype.slice.call(sectionEl.querySelectorAll('[data-gwtd-chip]'));
    var slotZeroDeck = decks[0];

    function scrollChipIntoView(chip) {
      if (!chip) return;
      chip.scrollIntoView({
        behavior: REDUCED_MOTION ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var idx = parseInt(chip.getAttribute('data-arcana-index'), 10);
        if (isNaN(idx) || !slotZeroDeck) return;
        chips.forEach(function (c) {
          c.classList.toggle('is-active', c === chip);
          c.setAttribute('aria-selected', c === chip ? 'true' : 'false');
        });
        scrollChipIntoView(chip);
        slotZeroDeck.applyArcana(idx);
      });
    });
    if (slotZeroDeck) {
      slotZeroDeck.onArcanaChange(function (idx) {
        chips.forEach(function (c) {
          var isActive = parseInt(c.getAttribute('data-arcana-index'), 10) === idx;
          c.classList.toggle('is-active', isActive);
          c.setAttribute('aria-selected', isActive ? 'true' : 'false');
          if (isActive) scrollChipIntoView(c);
        });
      });
    }

    // "Draw your fate"
    var drawBtn = sectionEl.querySelector('[data-gwtd-draw-btn]');
    if (drawBtn) {
      drawBtn.addEventListener('click', function () {
        if (drawBtn.disabled) return;
        drawBtn.disabled = true;
        var isMobile = window.matchMedia('(max-width: 749px)').matches;
        var targets = isMobile ? [decks[0]] : decks;
        var promises = targets.map(function (deck, i) {
          return new Promise(function (resolve) {
            window.setTimeout(function () {
              deck.spinToRandom().then(resolve);
            }, REDUCED_MOTION ? i * 90 : i * 300);
          });
        });
        Promise.all(promises).then(function () { drawBtn.disabled = false; });
      });
    }

    initPicksStrip(sectionEl);
  }

  var PICKS_LIMIT = 6;

  // "Your picks" strip — up to 6 most-recently-saved products as round
  // thumbnails, newest first, under the Draw your fate button. Hidden
  // entirely while the wishlist is empty.
  function initPicksStrip(sectionEl) {
    var stripEl = sectionEl.querySelector('[data-gwtd-picks-strip]');
    var trackEl = sectionEl.querySelector('[data-gwtd-picks-track]');
    var countEl = sectionEl.querySelector('[data-gwtd-picks-count]');
    var viewAllBtn = sectionEl.querySelector('[data-gwtd-picks-viewall]');
    var pickTemplate = sectionEl.querySelector('[data-gwtd-pick-template]');
    if (!stripEl || !trackEl || !pickTemplate || !window.gwWishlist) return;

    if (viewAllBtn) {
      viewAllBtn.addEventListener('click', function () {
        window.gwWishlist.openDrawer();
      });
    }

    var lastRenderedHandles = [];

    function render() {
      var all = window.gwWishlist.getAll();
      if (countEl) countEl.textContent = all.length;

      if (all.length === 0) {
        stripEl.hidden = true;
        lastRenderedHandles = [];
        return;
      }
      stripEl.hidden = false;

      // Newest-saved first, capped to PICKS_LIMIT.
      var newestFirst = all.slice().reverse().slice(0, PICKS_LIMIT);
      var isFreshAdd = newestFirst.length > 0 &&
        lastRenderedHandles.indexOf(newestFirst[0]) === -1;

      trackEl.innerHTML = '';
      newestFirst.forEach(function (handle, i) {
        var frag = pickTemplate.content.cloneNode(true);
        var el = frag.querySelector('[data-gwtd-pick]');
        var img = frag.querySelector('[data-gwtd-pick-img]');
        el.href = '/products/' + handle;
        if (i === 0 && isFreshAdd) el.classList.add('is-new');
        trackEl.appendChild(el);

        fetch('/products/' + handle + '.js')
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (product) {
            if (!product) return;
            var image = product.featured_image || (product.images && product.images[0]);
            if (image) {
              img.src = cdnResize(image, 160);
              img.alt = product.title;
            }
          })
          .catch(function () {});
      });

      lastRenderedHandles = newestFirst;
    }

    window.gwWishlist.onChange(render);
    render();
  }

  function initDeck(deckEl, arcanaPool, goblinConfig, forceGoblin, cardTemplate, goblinTemplate) {
    var cardsEl = deckEl.querySelector('[data-gwtd-cards]');
    var stageEl = deckEl.querySelector('.gwtd__stage');
    var statusEl = deckEl.querySelector('[data-gwtd-status]');
    var heartBurstEl = deckEl.querySelector('[data-gwtd-heart-burst]');
    var skipBtn = deckEl.querySelector('[data-gwtd-skip]');
    var saveBtn = deckEl.querySelector('[data-gwtd-save]');
    var controlsEl = deckEl.querySelector('[data-gwtd-controls]');
    var metaTitleEl = deckEl.querySelector('[data-gwtd-card-title]');
    var metaPriceEl = deckEl.querySelector('[data-gwtd-card-price]');
    var quickAddBtn = deckEl.querySelector('[data-gwtd-card-quickadd]');
    var quickAddLabel = deckEl.querySelector('[data-gwtd-quickadd-label]');
    var goblinPrizeEl = deckEl.querySelector('[data-gwtd-goblin-prize]');
    var goblinIntroEl = deckEl.querySelector('[data-gwtd-goblin-intro]');
    var goblinHeadlineEl = deckEl.querySelector('[data-gwtd-goblin-headline]');
    var goblinSublineEl = deckEl.querySelector('[data-gwtd-goblin-subline]');
    var goblinClaimBtn = deckEl.querySelector('[data-gwtd-goblin-claim]');
    var goblinStatusEl = deckEl.querySelector('[data-gwtd-goblin-status]');
    var goblinCodeEl = deckEl.querySelector('[data-gwtd-goblin-code]');

    var arcanaChangeListeners = [];

    var state = {
      arcanaIndex: parseInt(deckEl.getAttribute('data-arcana-index'), 10) || 0,
      collectionHandle: '',
      tarotName: '',
      roman: '',
      frameSrc: '',
      emblem: '',
      queue: [],
      seenHandles: {},
      pageCandidates: [],
      fetching: false,
      noCardsEver: false,
      isSpinning: false,
      goblinForceConsumed: false,
      current: null, // { product, cardEl, isGoblin }
      lastDealtType: null,
      lastDealtTypeCount: 0,
      pool: []
    };

    function resetPass() {
      state.pageCandidates = shuffle(range(MAX_PAGE_CANDIDATES));
      state.seenHandles = {};
      state.pool = [];
      // A fresh pass/deck shouldn't be constrained by whatever type was
      // last dealt from a DIFFERENT collection (chip switch) or a prior
      // full reshuffle.
      state.lastDealtType = null;
      state.lastDealtTypeCount = 0;
    }

    function applyArcana(index) {
      var entry = arcanaPool[index];
      if (!entry) return;
      state.arcanaIndex = index;
      state.collectionHandle = entry.collection || '';
      state.tarotName = entry.name || '';
      state.roman = entry.roman || '';
      state.frameSrc = entry.frameSrc || '';
      state.emblem = entry.emblem || '';
      state.queue = [];
      resetPass();
      state.noCardsEver = false;

      deckEl.setAttribute('data-arcana-index', index);
      deckEl.setAttribute('data-collection-handle', state.collectionHandle);
      deckEl.setAttribute('data-tarot-name', state.tarotName);
      deckEl.setAttribute('data-roman', state.roman);

      if (state.current && state.current.cardEl) {
        state.current.cardEl.remove();
        state.current = null;
      }
      resetMetaDisplay();
      hideStatus();

      if (!state.collectionHandle) {
        showStatus('No collection set for this arcana yet.', 0);
      } else {
        dealCard({});
      }

      arcanaChangeListeners.forEach(function (fn) {
        try { fn(index); } catch (e) {}
      });
    }
    function onArcanaChange(fn) { arcanaChangeListeners.push(fn); }

    function showStatus(message, autoHideMs) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.hidden = false;
      if (autoHideMs) window.setTimeout(hideStatus, autoHideMs);
    }
    function hideStatus() { if (statusEl) statusEl.hidden = true; }

    function ensureQueue() {
      if (state.queue.length < REFILL_THRESHOLD && !state.fetching) fetchNextBatch();
    }

    function fetchNextBatch() {
      if (!state.collectionHandle || state.fetching) return;

      if (state.pageCandidates.length === 0) {
        var hadAnythingThisPass = Object.keys(state.seenHandles).length > 0;
        resetPass();
        if (!hadAnythingThisPass && state.queue.length === 0) {
          state.noCardsEver = true;
          showStatus("This deck doesn't have any cards available right now.", 0);
          return;
        }
        showStatus('The cards have been reshuffled…', 1600);
        fetchNextBatch();
        return;
      }

      state.fetching = true;
      // v9.2: several random pages per batch, fetched in parallel and
      // merged — see the comment above interleaveByType() for why a
      // single page wasn't enough. Same code path regardless of device;
      // there was never actually a separate mobile fetch path, it just
      // reads as a longer homogeneous run on a phone because only one
      // deck is visible at a time there, where desktop's 4 side-by-side
      // decks make the same bug less visually obvious.
      var pages = state.pageCandidates.splice(0, BATCH_PAGES_PER_FETCH);
      var urls = pages.map(function (page) {
        return '/collections/' + state.collectionHandle + '/products.json?limit=' + BATCH_LIMIT + '&page=' + page;
      });

      Promise.all(urls.map(function (url) {
        return fetch(url).then(function (res) { return res.json(); }).catch(function () { return null; });
      })).then(function (jsons) {
        state.fetching = false;
        var products = [];
        jsons.forEach(function (json) {
          if (json && json.products) products = products.concat(json.products);
        });
        var wishlistHandles = (window.gwWishlist && window.gwWishlist.getAll()) || [];
        var fresh = products.filter(function (p) {
          if (state.seenHandles[p.handle]) return false;
          if (!productIsAvailable(p)) return false;
          if (wishlistHandles.indexOf(p.handle) !== -1) return false;
          return true;
        });
        fresh.forEach(function (p) { state.seenHandles[p.handle] = true; });
        state.pool = state.pool.concat(shuffle(fresh));

        var tail = state.queue[state.queue.length - 1];
        var seedType = tail ? guessProductType(tail) : state.lastDealtType;
        var seedCount = 0;
        if (tail) {
          for (var i = state.queue.length - 1; i >= 0 && guessProductType(state.queue[i]) === seedType; i--) seedCount++;
        } else {
          seedCount = state.lastDealtTypeCount;
        }
        var placed = interleaveByType(state.pool, seedType, seedCount, state.pageCandidates.length === 0);
        state.queue = state.queue.concat(placed.result);
        state.pool = placed.leftover;
        if (!state.current) dealCard({});
        else ensureQueue();
      });
    }

    function shouldDrawGoblin() {
      if (!goblinConfig.enabled) return false;
      if (forceGoblin && !state.goblinForceConsumed) {
        state.goblinForceConsumed = true;
        return true;
      }
      if (!forceGoblin && isGoblinSuppressed()) return false;
      var odds = goblinConfig.odds > 0 ? goblinConfig.odds : 150;
      return Math.random() < 1 / odds;
    }

    function resetMetaDisplay() {
      if (metaTitleEl) metaTitleEl.textContent = '';
      if (metaPriceEl) metaPriceEl.textContent = '';
      if (goblinPrizeEl) goblinPrizeEl.hidden = true;
      if (controlsEl) controlsEl.hidden = false;
    }

    function renderGoblinCard(flags) {
      var frag = goblinTemplate.content.cloneNode(true);
      var cardEl = frag.querySelector('[data-gwtd-card]');

      cardEl.classList.add('is-top', 'is-appearing');
      if (flags.landing) cardEl.classList.add('is-landing');
      if (flags.fadeIn) cardEl.classList.add('is-fade-in');
      cardsEl.innerHTML = '';
      cardsEl.appendChild(cardEl);

      if (metaTitleEl) metaTitleEl.textContent = '';
      if (metaPriceEl) metaPriceEl.textContent = '';
      if (controlsEl) controlsEl.hidden = true;
      if (goblinPrizeEl) {
        goblinPrizeEl.hidden = false;
        goblinIntroEl.textContent = goblinConfig.intro || 'The Goblin has chosen you.';
        goblinHeadlineEl.textContent = goblinConfig.headline || '10% OFF';
        goblinSublineEl.textContent = goblinConfig.subline || '';
        goblinCodeEl.textContent = 'Code: ' + (goblinConfig.code || '');
        goblinClaimBtn.hidden = false;
        goblinClaimBtn.disabled = false;
        goblinClaimBtn.textContent = 'Claim it';
        goblinStatusEl.hidden = true;
        goblinStatusEl.textContent = '';

        goblinClaimBtn.onclick = function () {
          goblinClaimBtn.disabled = true;
          goblinClaimBtn.textContent = 'Claiming…';
          fetch('/discount/' + encodeURIComponent(goblinConfig.code || ''), { credentials: 'same-origin' })
            .catch(function () {})
            .then(function () {
              markGoblinClaimed();
              goblinClaimBtn.hidden = true;
              goblinStatusEl.textContent = 'Claimed! Your 10% off will apply at checkout.';
              goblinStatusEl.hidden = false;
            });
        };
      }

      wireDrag(cardEl);
      state.current = { product: null, cardEl: cardEl, isGoblin: true };
      hideStatus();
    }

    function renderCard(product, flags) {
      var frag = cardTemplate.content.cloneNode(true);
      var cardEl = frag.querySelector('[data-gwtd-card]');
      var numeralEl = cardEl.querySelector('[data-gwtd-card-numeral]');
      var imgEl = cardEl.querySelector('[data-gwtd-card-img]');
      var nameEl = cardEl.querySelector('[data-gwtd-card-name]');
      var emblemEl = cardEl.querySelector('[data-gwtd-card-emblem]');
      var mediaEl = cardEl.querySelector('[data-gwtd-card-media]');
      var badgeEl = cardEl.querySelector('[data-gwtd-card-badge]');
      if (badgeEl) badgeEl.hidden = !isPreorder(product);

      numeralEl.textContent = state.roman;
      nameEl.textContent = state.tarotName;
      if (emblemEl && EMBLEMS[state.emblem]) emblemEl.innerHTML = EMBLEMS[state.emblem];

      if (state.frameSrc) {
        cardEl.style.setProperty('--gwtd-frame-img', 'url(' + state.frameSrc + ')');
      }

      var image = product.images && product.images[0];
      if (image) {
        imgEl.src = cdnResize(image.src, 500);
        imgEl.alt = product.title;
      }

      cardEl.classList.add('is-top');
      if (flags.landing) cardEl.classList.add('is-landing');
      if (flags.fadeIn) cardEl.classList.add('is-fade-in');
      cardsEl.innerHTML = '';
      cardsEl.appendChild(cardEl);

      resetMetaDisplay();
      if (metaTitleEl) metaTitleEl.textContent = cleanTitle(product.title);
      var variant = availableVariants(product)[0];
      if (metaPriceEl) metaPriceEl.textContent = variant ? formatMoney(variant.price) : '';

      var variants = availableVariants(product);
      if (quickAddLabel && quickAddBtn) {
        if (variants.length === 1) {
          quickAddLabel.textContent = 'Add to bag';
          quickAddBtn.setAttribute('data-mode', 'add');
          quickAddBtn.setAttribute('data-variant-id', variants[0].id);
        } else {
          quickAddLabel.textContent = 'Choose options';
          quickAddBtn.setAttribute('data-mode', 'link');
          quickAddBtn.removeAttribute('data-variant-id');
        }
      }

      // Tap the product image/card to open the product page — distinct
      // from a drag, via wireDrag's own tap-vs-swipe distance check.
      if (mediaEl) {
        mediaEl.addEventListener('click', function (e) {
          if (cardEl.dataset.gwtdSuppressClick === '1') {
            delete cardEl.dataset.gwtdSuppressClick;
            return;
          }
          window.location.href = '/products/' + product.handle;
        });
      }

      wireDrag(cardEl);
      state.current = { product: product, cardEl: cardEl, isGoblin: false };
      hideStatus();
    }

    function dealCard(flags) {
      ensureQueue();
      if (!state.noCardsEver && shouldDrawGoblin()) {
        renderGoblinCard(flags);
        return;
      }
      if (state.queue.length === 0) {
        if (!state.fetching && !state.noCardsEver) fetchNextBatch();
        return;
      }
      var product = state.queue.shift();
      var dealtType = guessProductType(product);
      if (dealtType === state.lastDealtType) state.lastDealtTypeCount++;
      else { state.lastDealtType = dealtType; state.lastDealtTypeCount = 1; }
      renderCard(product, flags);
      ensureQueue();
    }

    function burstHeart() {
      if (!heartBurstEl) return;
      heartBurstEl.classList.remove('is-bursting');
      void heartBurstEl.offsetWidth;
      heartBurstEl.classList.add('is-bursting');
    }

    function resolveCard(direction) {
      if (!state.current || state.isSpinning) return;
      var current = state.current;
      state.current = null;

      if (direction === 'save' && !current.isGoblin) {
        if (window.gwWishlist) {
          // Capture the card's on-screen position BEFORE it starts
          // flying off, so the heart launches from where it actually is.
          window.gwWishlist.flyHeartFrom(current.cardEl.getBoundingClientRect());
          window.gwWishlist.add(current.product.handle);
        }
        burstHeart();
      }

      var cardEl = current.cardEl;
      cardEl.classList.remove('is-dragging', 'is-returning');

      if (USE_SWIPE_STAMP) {
        swipeAwayCard(cardEl, direction === 'save' ? 'right' : 'left');
        return;
      }

      if (direction === 'skip') {
        burnCard(cardEl);
        return;
      }

      cardEl.classList.add('is-exiting');
      void cardEl.offsetWidth;
      cardEl.style.transform = 'translateX(480px) rotate(18deg)';

      var done = false;
      function finish() {
        if (done) return;
        done = true;
        cardEl.remove();
        dealCard({});
      }
      cardEl.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, 450);
    }

    // v9 fallback for phones/touch and non-Chromium desktop browsers
    // (see supportsBurnEffect() above) — Tinder-style fly-off with a
    // stamp. Reused for both drag-released swipes AND ✕/♥ button taps:
    // a button tap has no drag distance to fade the stamp in from, so
    // it just snaps straight to full opacity, giving a brief flash as
    // the card leaves — still reads as "the same gesture", not two
    // different behaviours depending on input method.
    function swipeAwayCard(cardEl, dir) {
      var stamp = cardEl.querySelector(dir === 'left' ? '[data-gwtd-swipe-stamp-x]' : '[data-gwtd-swipe-stamp-heart]');
      if (stamp) stamp.style.opacity = '1';
      cardEl.classList.add('is-exiting');
      void cardEl.offsetWidth;
      var tx = dir === 'left' ? -480 : 480;
      var rotate = dir === 'left' ? -18 : 18;
      cardEl.style.transform = 'translateX(' + tx + 'px) rotate(' + rotate + 'deg)';

      var done = false;
      function finish() {
        if (done) return;
        done = true;
        cardEl.remove();
        dealCard({});
      }
      cardEl.addEventListener('transitionend', finish, { once: true });
      window.setTimeout(finish, 450);
    }

    // Left-swipe/skip "burns" the card away instead of flying off flat —
    // see buildBurnMask() above and gw-tarot-deck.css for the mask/ember/
    // smoke/sigil/glow layers. The card stays full size throughout; only
    // its .gwtd__card-face content dissolves behind the noisy mask edge.
    // Reduced-motion skips the mask entirely and gets a plain fade via
    // its own CSS override (no mask built = no filter work at all).
    function burnCard(cardEl) {
      cardEl.classList.add('is-burning');
      var burnMs = REDUCED_MOTION ? 200 : 950;
      if (!REDUCED_MOTION) {
        buildBurnMask(cardEl, burnMs);
      }
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        cardEl.remove();
        dealCard({});
      }
      window.setTimeout(finish, burnMs);
    }

    function wireDrag(cardEl) {
      var dragging = false;
      var horizontalLock = null;
      var startX = 0, startY = 0, pointerId = null;

      cardEl.addEventListener('pointerdown', function (e) {
        if (state.isSpinning) return;
        if (state.current && state.current.cardEl !== cardEl) return;
        dragging = true;
        horizontalLock = null;
        startX = e.clientX;
        startY = e.clientY;
        pointerId = e.pointerId;
        cardEl.classList.add('is-dragging');
      });

      cardEl.addEventListener('pointermove', function (e) {
        if (!dragging || e.pointerId !== pointerId) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;

        if (horizontalLock === null) {
          if (Math.abs(dx) < DRAG_LOCK_PX && Math.abs(dy) < DRAG_LOCK_PX) return;
          horizontalLock = Math.abs(dx) > Math.abs(dy);
          if (horizontalLock) {
            try { cardEl.setPointerCapture(pointerId); } catch (err) {}
          } else {
            dragging = false;
            cardEl.classList.remove('is-dragging');
            return;
          }
        }
        if (!horizontalLock) return;
        var rotate = dx / 18;
        cardEl.style.transform = 'translate(' + dx + 'px, ' + (dy * 0.4) + 'px) rotate(' + rotate + 'deg)';

        // v9: stamp opacity fades in proportional to drag distance,
        // capped at full opacity by the swipe threshold — the same
        // distance that would trigger the fly-off on release.
        if (USE_SWIPE_STAMP) {
          var progress = Math.min(1, Math.abs(dx) / SWIPE_THRESHOLD_PX);
          var xStamp = cardEl.querySelector('[data-gwtd-swipe-stamp-x]');
          var heartStamp = cardEl.querySelector('[data-gwtd-swipe-stamp-heart]');
          if (xStamp) xStamp.style.opacity = dx < 0 ? String(progress) : '0';
          if (heartStamp) heartStamp.style.opacity = dx > 0 ? String(progress) : '0';
        }
      });

      function endDrag(e) {
        if (!dragging) return;
        dragging = false;
        cardEl.classList.remove('is-dragging');
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;

        if (!horizontalLock) {
          // Never engaged a horizontal drag — if it barely moved at all,
          // it was a tap, so let the media element's own click handler
          // fire normally (it will, since we never preventDefault here).
          return;
        }

        // Any engaged horizontal drag — whether it dismissed the card or
        // just sprang back — means this was a swipe attempt, not a tap,
        // so suppress the synthetic click the browser fires right after
        // pointerup (it would otherwise also navigate to the product).
        cardEl.dataset.gwtdSuppressClick = '1';
        if (Math.abs(dx) > SWIPE_THRESHOLD_PX) {
          resolveCard(dx > 0 ? 'save' : 'skip');
        } else {
          cardEl.classList.add('is-returning');
          void cardEl.offsetWidth;
          cardEl.style.transform = '';
          if (USE_SWIPE_STAMP) {
            var xStampBack = cardEl.querySelector('[data-gwtd-swipe-stamp-x]');
            var heartStampBack = cardEl.querySelector('[data-gwtd-swipe-stamp-heart]');
            if (xStampBack) xStampBack.style.opacity = '0';
            if (heartStampBack) heartStampBack.style.opacity = '0';
          }
          cardEl.addEventListener('transitionend', function () {
            cardEl.classList.remove('is-returning');
          }, { once: true });
        }
      }

      cardEl.addEventListener('pointerup', endDrag);
      cardEl.addEventListener('pointercancel', endDrag);
    }

    function spinToRandom() {
      return new Promise(function (resolve) {
        if (state.isSpinning) { resolve(); return; }
        state.isSpinning = true;

        if (state.current && state.current.cardEl) {
          state.current.cardEl.remove();
          state.current = null;
        }

        if (REDUCED_MOTION) {
          dealCard({ fadeIn: true });
          state.isSpinning = false;
          resolve();
          return;
        }

        ensureQueue();

        function runFlicker(attempt) {
          var pool = state.queue.slice(0, 12);
          if (pool.length === 0) {
            if (attempt >= 8) {
              dealCard({ landing: true });
              state.isSpinning = false;
              resolve();
              return;
            }
            window.setTimeout(function () { runFlicker(attempt + 1); }, 150);
            return;
          }

          var frameCount = 10;
          var frameDelay = 70;
          var i = 0;

          function flickerFrame() {
            if (i >= frameCount) {
              dealCard({ landing: true });
              state.isSpinning = false;
              resolve();
              return;
            }
            var product = pool[i % pool.length];
            var frag = cardTemplate.content.cloneNode(true);
            var cardEl = frag.querySelector('[data-gwtd-card]');
            cardEl.classList.add('is-top', 'is-flicker');
            if (state.frameSrc) cardEl.style.setProperty('--gwtd-frame-img', 'url(' + state.frameSrc + ')');
            cardEl.querySelector('[data-gwtd-card-numeral]').textContent = state.roman;
            cardEl.querySelector('[data-gwtd-card-name]').textContent = state.tarotName;
            var emblemEl = cardEl.querySelector('[data-gwtd-card-emblem]');
            if (emblemEl && EMBLEMS[state.emblem]) emblemEl.innerHTML = EMBLEMS[state.emblem];
            var image = product.images && product.images[0];
            var imgEl = cardEl.querySelector('[data-gwtd-card-img]');
            if (image) imgEl.src = cdnResize(image.src, 300);
            cardsEl.innerHTML = '';
            cardsEl.appendChild(cardEl);

            i++;
            frameDelay = frameDelay * 1.16;
            window.setTimeout(flickerFrame, frameDelay);
          }
          flickerFrame();
        }
        runFlicker(0);
      });
    }

    if (skipBtn) skipBtn.addEventListener('click', function () { resolveCard('skip'); });
    if (saveBtn) saveBtn.addEventListener('click', function () { resolveCard('save'); });

    if (stageEl) {
      stageEl.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); resolveCard('save'); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); resolveCard('skip'); }
      });
    }

    // Success: gold "In the bag" stamp hits the card, then it slides
    // down and out, and the next card deals in — same dismissal feel as
    // save/skip, just a different direction. Failure (e.g. sold out
    // between loading and tapping): a short status message, card stays
    // exactly as it was so the customer can try something else.
    function stampAndAdvance(cardEl) {
      var stamp = document.createElement('div');
      stamp.className = 'gwtd__card-stamp';
      stamp.setAttribute('aria-hidden', 'true');
      stamp.innerHTML = 'In the bag <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true" style="vertical-align:-1px"><path d="M4 12l5 5L20 6"/></svg>';
      cardEl.appendChild(stamp);
      cardEl.classList.add('is-added');
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        cardEl.remove();
        dealCard({});
      }
      var addedMs = REDUCED_MOTION ? 260 : 820;
      window.setTimeout(finish, addedMs);
    }

    if (quickAddBtn) {
      quickAddBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!state.current || state.current.isGoblin) return;
        var mode = quickAddBtn.getAttribute('data-mode');
        if (mode === 'add') {
          var variantId = parseInt(quickAddBtn.getAttribute('data-variant-id'), 10);
          var cardEl = state.current.cardEl;
          if (variantId && window.gwAddToCartWithResult && cardEl) {
            quickAddBtn.disabled = true;
            window.gwAddToCartWithResult(variantId, 1, state.collectionHandle, undefined, { noDrawer: true })
              .then(function () {
                quickAddBtn.disabled = false;
                if (window.gwWishlist && window.gwWishlist.showBagToast) window.gwWishlist.showBagToast();
                stampAndAdvance(cardEl);
              })
              .catch(function (err) {
                quickAddBtn.disabled = false;
                showStatus((err && err.message) || 'That item is unavailable right now.', 2600);
              });
          }
        } else {
          window.location.href = '/products/' + state.current.product.handle;
        }
      });
    }

    resetPass();
    applyArcana(state.arcanaIndex);

    return {
      applyArcana: applyArcana,
      onArcanaChange: onArcanaChange,
      spinToRandom: spinToRandom
    };
  }

  function init() {
    document.querySelectorAll('.gwtd[data-gwtd-section]').forEach(initSection);
  }

  /* Always wait for DOMContentLoaded rather than branching on
     document.readyState — see git history for why (this tag sits
     earlier in the DOM than theme.liquid's gw-wishlist.js, both
     deferred; waiting for this event guarantees gw-wishlist.js has
     already run regardless of tag order). */
  document.addEventListener('DOMContentLoaded', init);
})();
